/**
 * Page numbers over a keyset-paginated ledger.
 *
 * `/api/wallet/ledger` pages by cursor — "the groups before this one" — not by
 * offset, so the only way to know where page N starts is to have fetched page
 * N - 1. This keeps the cursors fetches have returned, indexed by page, so the
 * card can go back as well as forward and show a page number.
 *
 * `starts[p - 1]` fetches page `p`. Page 1 needs no cursor, hence `null`.
 */

export interface LedgerCursor { at: number; groupId: string; }

export type PageStarts = readonly (LedgerCursor | null)[];

export const FIRST_PAGE: PageStarts = Object.freeze([null]);

/** The cursor that fetches `page`; `undefined` for a page not yet reachable. */
export function cursorForPage(starts: PageStarts, page: number): LedgerCursor | null | undefined {
  if (!Number.isInteger(page) || page < 1 || page > starts.length) return undefined;
  return starts[page - 1];
}

/**
 * The chain after `page` was fetched and returned `nextCursor`.
 *
 * Everything past `page` is dropped, not kept: rows may have landed since the
 * deeper pages were fetched, and a cursor derived from a stale page would skip
 * or repeat groups at the boundary. The only way past `page` is the cursor this
 * fetch just returned.
 */
export function recordPage(starts: PageStarts, page: number, nextCursor: LedgerCursor | null): PageStarts {
  const kept = starts.slice(0, page);
  return nextCursor ? [...kept, nextCursor] : kept;
}

export function hasNextPage(starts: PageStarts, page: number): boolean {
  return starts.length > page;
}
